/**
 * Textos de tela da árvore de permissões que o backend não entrega prontos (card 3902).
 *
 * Ordem de preferência: o que vem do backend (resx `PermissionsClaimsResources`: `<Enum>Title` para o grupo,
 * `<Enum>_<AÇÃO>` para a ação). Estes dicionários só cobrem o que falta lá.
 */

/** Papel (nome do `Role` no backend) → rótulo em português. Papel novo sem entrada aparece com a 1ª letra maiúscula. */
export const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrador',
  TENANT: 'Equipe interna',
  COMPANY: 'Empresa',
  PARTNER: 'Parceiro comercial',
  CUSTOMER: 'Cliente',
};

/**
 * Papéis que existem no banco mas não se usam (card 3902): o CUSTOMER é legado e, ao lado de "Empresa", só confunde
 * quem monta grupos. Ficam fora da lista de grupos e do seletor de tipo da tela de edição.
 */
const HIDDEN_ROLES = new Set(['CUSTOMER']);

/** Diz se o papel fica escondido nas telas de grupos de permissão. */
export function isHiddenRole(roleName: string | null | undefined): boolean {
  return HIDDEN_ROLES.has((roleName ?? '').trim().toUpperCase());
}

export function roleLabel(roleName: string): string {
  const name = (roleName ?? '').trim();
  if (ROLE_LABELS[name.toUpperCase()]) return ROLE_LABELS[name.toUpperCase()];
  return name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : '';
}

/**
 * Título de grupo para quando o backend não tem `<Enum>Title` no resx (ele devolve o nome do enum, ex.:
 * `EconomicData`) ou quando o título dele é jargão. Mantenha curto: é o nome da entrada em "Outras (fora do menu)".
 */
const GROUP_TITLES: Record<string, string> = {
  EconomicData: 'Dados econômicos',
  ReportTemplate: 'Modelos de relatório',
  Onboarding: 'Onboarding de clientes',
  CogMail: 'E-mails processados pelo Cog',
  BackOfficeBusinessCase: 'Business case (backoffice)',
  BankRelationship: 'Relacionamento bancário',
  BusinessCase: 'Business case',
  Comex: 'Comex',
  Commercial: 'Comercial',
  CorporateRegistry: 'Registro societário',
  DataRoom: 'Data Room',
  DataRoomMaintenance: 'Data Room: manutenção',
  DerivativeRecalculateOperations: 'Derivativos: recálculo de operações',
  Dfp: 'Demonstrações financeiras (DFP)',
  DfpAssumption: 'DFP: premissas',
  DfpComments: 'DFP: comentários',
  DocumentPadronization: 'Padronização de documentos',
  EconomicGroup: 'Grupos econômicos',
  EffectiveExchangeRates: 'Taxas de câmbio efetivas',
  FinancialInstitutionFundingOrigin: 'Origens de captação',
  ForeignExchangeOperations: 'Operações de câmbio',
  Guarantee: 'Garantias',
  MarketProjectionCurve: 'Projeções de mercado',
  Partner: 'Parceiros',
  Pipeline: 'Pipeline de crédito',
  PositionReportDispatch: 'Envio do relatório de posição',
  Tenant: 'Tenants',
};

/** Título do grupo: o do backend; se ele não tiver (devolve o próprio nome) ou for jargão, o do dicionário. */
export function groupTitle(group: { name: string; description?: string | null }): string {
  const fromBackend = (group.description ?? '').trim();
  const override = GROUP_TITLES[group.name];
  if (override && (!fromBackend || fromBackend === group.name || group.name === 'CogMail')) {
    return override;
  }
  return fromBackend || override || humanizeName(group.name);
}

/** "EconomicData" → "Economic data" (último recurso). */
function humanizeName(name: string): string {
  const words = name.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Objeto de que a ação trata, para "Excluir empréstimo" em vez de "Excluir". */
export interface PermissionObject {
  singular: string;
  plural: string;
  /** Gênero, para "um empréstimo" / "uma garantia". */
  feminine?: boolean;
}

const m = (singular: string, plural: string): PermissionObject => ({ singular, plural });
const f = (singular: string, plural: string): PermissionObject => ({
  singular,
  plural,
  feminine: true,
});

/**
 * Objeto por grupo do catálogo (nome do enum sem `Permissions`). Cobre os grupos cujas ações genéricas (`LIST`,
 * `VIEW`, `CREATE`, `UPDATE`, `DELETE`, `MODULE`) não têm descrição própria no resx. Grupo sem entrada mostra o
 * verbo sozinho ("Excluir").
 */
const GROUP_OBJECTS: Record<string, PermissionObject> = {
  BackOfficeBusinessCase: m('business case', 'business cases'),
  BankRelationship: m('relacionamento bancário', 'relacionamentos bancários'),
  BusinessCase: m('business case', 'business cases'),
  CogMail: m('e-mail processado', 'e-mails processados'),
  Comex: f('operação de comex', 'operações de comex'),
  Commercial: m('deal', 'deals'),
  Company: f('empresa', 'empresas'),
  CorporateRegistry: m('registro societário', 'registros societários'),
  DataRoom: m('documento do Data Room', 'documentos do Data Room'),
  DataRoomMaintenance: f('manutenção do Data Room', 'manutenções do Data Room'),
  DerivativeAttachmentType: m('tipo de anexo de derivativo', 'tipos de anexo de derivativo'),
  DerivativeCalculator: m('derivativo', 'derivativos'),
  DerivativeRecalculateOperations: m('derivativo', 'derivativos'),
  DfpAssumption: f('premissa da DFP', 'premissas da DFP'),
  DfpComments: m('comentário da DFP', 'comentários da DFP'),
  Dfp: f('DFP', 'DFPs'),
  DocumentPadronization: m('documento padronizado', 'documentos padronizados'),
  EconomicData: m('dado econômico', 'dados econômicos'),
  EconomicGroup: m('grupo econômico', 'grupos econômicos'),
  EffectiveExchangeRates: f('taxa de câmbio efetiva', 'taxas de câmbio efetivas'),
  FinancialInstitutionFundingOrigin: f('origem de captação', 'origens de captação'),
  FinancialInstitution: f('instituição financeira', 'instituições financeiras'),
  ForeignExchangeOperations: f('operação de câmbio', 'operações de câmbio'),
  Guarantee: f('garantia', 'garantias'),
  LoanOperation: m('empréstimo', 'empréstimos'),
  MarketProjectionCurve: f('projeção de mercado', 'projeções de mercado'),
  Onboarding: m('onboarding', 'onboardings'),
  Partner: m('parceiro', 'parceiros'),
  PermissionGroup: m('grupo de permissão', 'grupos de permissão'),
  Pipeline: f('oportunidade do pipeline', 'oportunidades do pipeline'),
  PositionReportDispatch: m('envio do relatório de posição', 'envios do relatório de posição'),
  Ptax: f('cotação PTAX', 'cotações PTAX'),
  ReportTemplate: m('modelo de relatório', 'modelos de relatório'),
  RiskContingency: m('parâmetro de contingência', 'parâmetros de contingência'),
  RiskPanel: f('estrutura do painel de risco', 'estruturas do painel de risco'),
  Swagger: f('documentação da API', 'documentações da API'),
  Tenant: m('tenant', 'tenants'),
  User: m('usuário', 'usuários'),
};

export function groupObject(groupName: string): PermissionObject | null {
  return GROUP_OBJECTS[groupName] ?? null;
}
